import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Divider,
	notification,
	Spin,
	Alert,
	List,
	Switch,
	Tabs,
	Modal,
	Form,
	DatePicker,
	InputNumber,
	Select,
	Input,
	Popconfirm,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiGlobeDuotone,
	PiUserDuotone,
	PiReceiptDuotone,
	PiShoppingCartDuotone,
	PiCalendarDuotone,
	PiCurrencyDollarDuotone,
	PiEnvelopeDuotone,
	PiShieldDuotone,
	PiLockKeyDuotone,
	PiCertificateDuotone,
	PiLinkDuotone,
	PiPencilDuotone,
	PiBuildingsDuotone,
} from "react-icons/pi";
import {
	requestGetDomainById,
	requestUpdateDomainBillingInformation,
	requestEditDomain,
	requestStartDomainRegistration,
} from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;

function DomainViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [domain, setDomain] = useState(null);

	// Billing Update Modal
	const [updateBillingModalVisible, setUpdateBillingModalVisible] = useState(false);
	const [updateBillingLoading, setUpdateBillingLoading] = useState(false);
	const [billingForm] = Form.useForm();

	// Registrar Edit Modal
	const [editRegistrarModalVisible, setEditRegistrarModalVisible] = useState(false);
	const [editRegistrarLoading, setEditRegistrarLoading] = useState(false);
	const [registrarForm] = Form.useForm();

	// Start registration
	const [startRegistrationLoading, setStartRegistrationLoading] = useState(false);

	useEffect(() => {
		document.title = "Cloudiva Console | Domain Details";
		fetchDomainDetails();
	}, [id]);

	const fetchDomainDetails = () => {
		setLoading(true);
		requestGetDomainById(id)
			.then((response) => {
				const domainData = response.data.data || response.data;
				setDomain(domainData);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch domain details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "ACTIVE":
				return "green";
			case "SUSPENDED":
				return "red";
			case "EXPIRED":
				return "default";
			case "CANCELLED":
				return "volcano";
			default:
				return "blue";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "ACTIVE":
				return "Active";
			case "SUSPENDED":
				return "Suspended";
			case "EXPIRED":
				return "Expired";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const handleUpdateBilling = () => {
		setUpdateBillingModalVisible(true);
		billingForm.setFieldsValue({
			expiredAt: domain.expiredAt ? dayjs(domain.expiredAt) : null,
			nextBilledAt: domain.nextBilledAt ? dayjs(domain.nextBilledAt) : null,
			cycle: domain.cycle || null,
		});
	};

	const handleUpdateBillingSubmit = (values) => {
		setUpdateBillingLoading(true);
		const payload = {
			expiredAt: values.expiredAt ? values.expiredAt.toISOString() : null,
			nextBilledAt: values.nextBilledAt ? values.nextBilledAt.toISOString() : null,
			cycle: {
				amount: values.cycle.amount,
				interval: values.cycle.interval,
			},
		};

		requestUpdateDomainBillingInformation(domain._id, payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Domain billing information updated successfully",
				});
				setUpdateBillingModalVisible(false);
				billingForm.resetFields();
				// Update domain data with new billing info
				setDomain((prev) => ({
					...prev,
					expiredAt: payload.expiredAt,
					nextBilledAt: payload.nextBilledAt,
					cycle: payload.cycle,
				}));
			})
			.catch((error) => {
				console.error("Error updating domain billing:", error);
				notification.error({
					message: "Error",
					description:
						error.response?.data?.message || "Failed to update domain billing information",
				});
			})
			.finally(() => {
				setUpdateBillingLoading(false);
			});
	};

	const handleUpdateBillingCancel = () => {
		setUpdateBillingModalVisible(false);
		billingForm.resetFields();
	};

	const handleEditRegistrar = () => {
		setEditRegistrarModalVisible(true);
		registrarForm.setFieldsValue({
			code: domain.registrar?.code || "",
			name: domain.registrar?.name || "",
		});
	};

	const handleEditRegistrarSubmit = (values) => {
		setEditRegistrarLoading(true);
		const payload = {
			registrar: {
				code: values.code,
				name: values.name,
			},
		};

		requestEditDomain(domain._id, payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Domain registrar updated successfully",
				});
				setEditRegistrarModalVisible(false);
				registrarForm.resetFields();
				// Update domain data with new registrar info
				setDomain((prev) => ({
					...prev,
					registrar: payload.registrar,
				}));
			})
			.catch((error) => {
				console.error("Error updating domain registrar:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to update domain registrar",
				});
			})
			.finally(() => {
				setEditRegistrarLoading(false);
			});
	};

	const handleEditRegistrarCancel = () => {
		setEditRegistrarModalVisible(false);
		registrarForm.resetFields();
	};

	const handleStartRegistration = () => {
		setStartRegistrationLoading(true);
		requestStartDomainRegistration(domain._id)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Domain registration started successfully",
				});
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to start domain registration",
				});
			})
			.finally(() => {
				setStartRegistrationLoading(false);
			});
	};

	if (loading) {
		return (
			<div className="page-content">
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "400px",
					}}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!domain) {
		return (
			<div className="page-content">
				<Alert
					message="Domain Not Found"
					description="The requested domain could not be found."
					type="error"
					showIcon
				/>
			</div>
		);
	}

	const isExpiringSoon = dayjs(domain.expiredAt).isBefore(dayjs().add(30, "day"));

	const tabItems = [
		{
			key: "overview",
			label: "Overview",
			children: (
				<Row gutter={[24, 24]}>
					<Col xs={24} lg={12}>
						<Card title="Domain Information">
							<Space direction="vertical" style={{ width: "100%" }}>
								<Descriptions column={1} size="small" bordered>
									<Descriptions.Item label="Domain Name">
										<div style={{ display: "flex", alignItems: "center" }}>
											<PiGlobeDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
											<Text strong>{domain.name}</Text>
										</div>
									</Descriptions.Item>
									<Descriptions.Item label="Domain Number">
										<Text code>{domain.number}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Extension">
										<div>
											<Tag color="blue">{domain.extension?.name}</Tag>
											<Text type="secondary" style={{ marginLeft: 8 }}>
												({domain.extension?.code})
											</Text>
										</div>
									</Descriptions.Item>
									<Descriptions.Item label="Status">
										<Tag color={getStatusColor(domain.status)}>{getStatusText(domain.status)}</Tag>
										{isExpiringSoon && (
											<Tag color="orange" style={{ marginLeft: 8 }}>
												Expiring Soon
											</Tag>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="Cycle">
										<Text>
											{domain.cycle?.amount} {domain.cycle?.interval?.toLowerCase()}
											{domain.cycle?.amount > 1 ? "s" : ""}
										</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Registrar">
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
											}}>
											<div style={{ display: "flex", alignItems: "center" }}>
												<PiBuildingsDuotone
													size={16}
													style={{ marginRight: 8, color: "#1890ff" }}
												/>
												<div>
													{domain.registrar ? (
														<div>
															<Text strong>{domain.registrar.name}</Text>
															<br />
															<Text type="secondary" style={{ fontSize: "12px" }}>
																Code: {domain.registrar.code}
															</Text>
														</div>
													) : (
														<Text type="secondary">Not specified</Text>
													)}
												</div>
											</div>
											<Button
												type="text"
												size="small"
												icon={<PiPencilDuotone />}
												onClick={handleEditRegistrar}
											/>
										</div>
									</Descriptions.Item>
								</Descriptions>
								{domain.status === "PENDING" && (
									<Popconfirm
										title="Are you sure you want to start the registration?"
										onConfirm={handleStartRegistration}
										okText="Yes"
										cancelText="No">
										<Button
											type="primary"
											style={{ width: "100%" }}
											loading={startRegistrationLoading}>
											Start Registration
										</Button>
									</Popconfirm>
								)}
							</Space>
						</Card>

						<Card title="Domain Settings" style={{ marginTop: 16 }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiCertificateDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
										<Text>Auto Renew</Text>
									</div>
									<Switch checked={domain.settings?.autoRenew} disabled size="small" />
								</div>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiShieldDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
										<Text>Privacy Protection</Text>
									</div>
									<Switch checked={domain.settings?.privacyProtection} disabled size="small" />
								</div>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiLockKeyDuotone size={16} style={{ marginRight: 8, color: "#ff4d4f" }} />
										<Text>Domain Lock</Text>
									</div>
									<Switch checked={domain.settings?.locked} disabled size="small" />
								</div>
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}>
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiEnvelopeDuotone size={16} style={{ marginRight: 8, color: "#722ed1" }} />
										<Text>Email Notifications</Text>
									</div>
									<Switch checked={domain.settings?.emailNotifications} disabled size="small" />
								</div>
							</div>
						</Card>
					</Col>

					<Col xs={24} lg={12}>
						<Card title="Important Dates">
							<Descriptions column={1} size="small" bordered>
								<Descriptions.Item label="Registration Date">
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiCalendarDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
										<Text>{dayjs(domain.registeredAt).format("MMMM DD, YYYY HH:mm")}</Text>
									</div>
								</Descriptions.Item>
								<Descriptions.Item label="Expiration Date">
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiCalendarDuotone
											size={16}
											style={{ marginRight: 8, color: isExpiringSoon ? "#ff4d4f" : "#ff7a45" }}
										/>
										<Text style={{ color: isExpiringSoon ? "#ff4d4f" : "inherit" }}>
											{dayjs(domain.expiredAt).format("MMMM DD, YYYY HH:mm")}
										</Text>
									</div>
								</Descriptions.Item>
								{domain.nextBilledAt && (
									<Descriptions.Item label="Next Billing Date">
										<div style={{ display: "flex", alignItems: "center" }}>
											<PiReceiptDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
											<Text>{dayjs(domain.nextBilledAt).format("MMMM DD, YYYY HH:mm")}</Text>
										</div>
									</Descriptions.Item>
								)}
								<Descriptions.Item label="Created">
									<Text type="secondary">
										{dayjs(domain.createdAt).format("MMMM DD, YYYY HH:mm")}
									</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Last Updated">
									<Text type="secondary">
										{dayjs(domain.updatedAt).format("MMMM DD, YYYY HH:mm")}
									</Text>
								</Descriptions.Item>
							</Descriptions>
						</Card>

						<Card title="Billing Information" style={{ marginTop: 16 }}>
							<Descriptions column={1} size="small" bordered>
								<Descriptions.Item label="Price per Year">
									<Text strong>{formatCurrency(domain.extension?.price?.amount, "IDR")}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Total Cost">
									<Text strong>{formatCurrency(domain.extension?.price?.total, "IDR")}</Text>
								</Descriptions.Item>
								{domain.orderId && (
									<Descriptions.Item label="Order">
										<Button
											type="link"
											size="small"
											onClick={() => navigate(`/orders/${domain.orderId}`)}
											icon={<PiShoppingCartDuotone />}>
											View Order
										</Button>
									</Descriptions.Item>
								)}
								{domain.invoiceId && (
									<Descriptions.Item label="Invoice">
										<Button
											type="link"
											size="small"
											onClick={() => navigate(`/invoices/${domain.invoiceId}`)}
											icon={<PiReceiptDuotone />}>
											View Invoice
										</Button>
									</Descriptions.Item>
								)}
							</Descriptions>

							<Divider />

							<Space>
								<Button type="default" icon={<PiPencilDuotone />} onClick={handleUpdateBilling}>
									Update Billing
								</Button>
							</Space>
						</Card>
					</Col>
				</Row>
			),
		},
		{
			key: "nameservers",
			label: "Nameservers",
			children: (
				<Row gutter={[24, 24]}>
					<Col span={24}>
						<Card title="Nameserver Configuration">
							{domain.nameservers && domain.nameservers.length > 0 ? (
								<List
									size="small"
									bordered
									dataSource={domain.nameservers}
									renderItem={(nameserver, index) => (
										<List.Item>
											<div style={{ display: "flex", alignItems: "center", width: "100%" }}>
												<PiLinkDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
												<Text strong>NS{index + 1}:</Text>
												<Text style={{ marginLeft: 8 }} code>
													{nameserver}
												</Text>
											</div>
										</List.Item>
									)}
								/>
							) : (
								<Alert
									message="No Nameservers Configured"
									description="This domain doesn't have any nameservers configured."
									type="info"
									showIcon
								/>
							)}
						</Card>
					</Col>
				</Row>
			),
		},
		{
			key: "team",
			label: "Team & Ownership",
			children: (
				<Row gutter={[24, 24]}>
					<Col span={24}>
						<Card title="Team Information">
							{domain.team ? (
								<Descriptions column={2} size="small" bordered>
									<Descriptions.Item label="Team Name" span={2}>
										<div style={{ display: "flex", alignItems: "center" }}>
											<PiUserDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
											<Text strong>{domain.team.name}</Text>
										</div>
									</Descriptions.Item>
									<Descriptions.Item label="Team Description" span={2}>
										<Text type="secondary">{domain.team.description || "No description"}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Team Owner">
										<Text strong>{domain.team.owner?.name}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Owner Email">
										<Text>{domain.team.owner?.email}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Team Members" span={2}>
										<Text>{domain.team.members?.length || 0} member(s)</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Team Created" span={2}>
										<Text type="secondary">
											{dayjs(domain.team.createdAt).format("MMMM DD, YYYY HH:mm")}
										</Text>
									</Descriptions.Item>
								</Descriptions>
							) : (
								<Alert
									message="No Team Information"
									description="This domain doesn't have associated team information."
									type="info"
									showIcon
								/>
							)}
						</Card>
					</Col>
				</Row>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/domains")} />
						<Title level={4} style={{ margin: 0 }}>
							Domain Details
						</Title>
						<Tag color={getStatusColor(domain.status)}>{getStatusText(domain.status)}</Tag>
						{isExpiringSoon && <Tag color="orange">Expiring Soon</Tag>}
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				{isExpiringSoon && (
					<Alert
						message="Domain Expiring Soon"
						description={`This domain will expire on ${dayjs(domain.expiredAt).format(
							"MMMM DD, YYYY"
						)}. Please renew to avoid service interruption.`}
						type="warning"
						showIcon
						style={{ marginBottom: 24 }}
						action={
							<Button type="primary" size="small">
								Renew Domain
							</Button>
						}
					/>
				)}

				<Tabs items={tabItems} defaultActiveKey="overview" size="small" />
			</div>

			{/* Billing Update Modal */}
			<Modal
				title={
					<Space>
						<PiReceiptDuotone />
						<span>Update Domain Billing Information</span>
					</Space>
				}
				open={updateBillingModalVisible}
				onCancel={handleUpdateBillingCancel}
				footer={null}
				width={600}>
				<Form
					form={billingForm}
					layout="vertical"
					onFinish={handleUpdateBillingSubmit}
					style={{ marginTop: 16 }}>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item label="Expired Date" name="expiredAt" extra="When the domain will expire">
								<DatePicker
									showTime
									format="DD/MM/YYYY HH:mm"
									placeholder="Select expired date"
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Next Billing Date"
								name="nextBilledAt"
								extra="When the next bill will be generated">
								<DatePicker
									showTime
									format="DD/MM/YYYY HH:mm"
									placeholder="Select next billing date"
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Divider plain>
						<strong>Billing Cycle</strong>
					</Divider>

					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								label="Cycle Amount"
								name={["cycle", "amount"]}
								extra="Billing cycle amount (e.g., 1, 3, 6)">
								<InputNumber min={1} placeholder="Enter amount" style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Cycle Interval"
								name={["cycle", "interval"]}
								extra="Billing cycle interval">
								<Select placeholder="Select interval">
									<Select.Option value="DAY">Day(s)</Select.Option>
									<Select.Option value="WEEK">Week(s)</Select.Option>
									<Select.Option value="MONTH">Month(s)</Select.Option>
									<Select.Option value="YEAR">Year(s)</Select.Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleUpdateBillingCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={updateBillingLoading}>
								Update Billing
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>

			{/* Registrar Edit Modal */}
			<Modal
				title={
					<Space>
						<PiBuildingsDuotone />
						<span>Edit Domain Registrar</span>
					</Space>
				}
				open={editRegistrarModalVisible}
				onCancel={handleEditRegistrarCancel}
				footer={null}
				width={500}>
				<Form
					form={registrarForm}
					layout="vertical"
					onFinish={handleEditRegistrarSubmit}
					style={{ marginTop: 16 }}>
					<Form.Item
						label="Registrar Code"
						name="code"
						rules={[{ required: true, message: "Please enter registrar code" }]}
						extra="The unique code identifying the registrar">
						<Input placeholder="e.g., GODADDY, NAMECHEAP, CLOUDFLARE" />
					</Form.Item>

					<Form.Item
						label="Registrar Name"
						name="name"
						rules={[{ required: true, message: "Please enter registrar name" }]}
						extra="The full name of the registrar">
						<Input placeholder="e.g., GoDaddy Inc., Namecheap Inc." />
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleEditRegistrarCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={editRegistrarLoading}>
								Update Registrar
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default DomainViewPage;
